import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Input } from 'reactstrap'
import { addPANNumberToWallet, validatePANNumber } from 'services/api/wallet';
import { fetchWalletThunk } from 'slices/wallet/thunk';
import { AppDispatch, RootState } from 'store';
import { PANValidationResponse } from 'types/wallet';

const PANInput: React.FC<{ onRegisteredForGST?: () => void }> = ({ onRegisteredForGST }) => {

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch = useDispatch<AppDispatch>();
    const [panNumber, setPanNumber] = React.useState<string>("");
    const [panError, setPanError] = React.useState<string | null>(null);

    const [validationData, setValidationData] = React.useState<PANValidationResponse | null>(null);
    const [validating, setValidating] = React.useState<boolean>(false);
    const [adding, setAdding] = React.useState<boolean>(false);

    const isValidPANNumber = useMemo(() => {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        return panRegex.test(panNumber);
    }, [panNumber]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase().trim();
        if (value === "") {
            setPanError("PAN Number cannot be empty.");
        } else if (!isValidPANNumber) {
            setPanError("Invalid PAN Number format.");
        } else {
            setPanError(null);
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPanNumber(e.target.value.toUpperCase().trim());
    }

    const validatePAN = async () => {
        if (!isValidPANNumber || !selectedAgencyId) return;

        setPanError(null);
        setValidationData(null);
        setValidating(true);
        try {
            const response = await validatePANNumber(selectedAgencyId, panNumber);
            setValidationData(response);
        } catch (error: any) {
            const response = error.response?.data as PANValidationResponse;
            setPanError(response?.message || "Failed to validate PAN Number.");
        } finally {
            setValidating(false);
        }
    }

    const addPANNumber = async () => {
        if (!validationData?.status || !selectedAgencyId || !validationData?.pan?.id) return;

        setAdding(true);
        try {
            const added = await addPANNumberToWallet(selectedAgencyId, validationData.pan.id);
            if (added) {
                toast.success("PAN Number added to wallet successfully.");
                dispatch(fetchWalletThunk({ force: true }))
                setPanNumber("");
                setValidationData(null);
            } else {
                toast.error("Failed to add PAN Number to wallet.");
            }
        } finally {
            setAdding(false);
        }
    }

    return (
        <>
            <div>
                <h6 className='wallet-stat-item-heading'>Add PAN</h6>
                <span className="text-muted">
                    We use PAN to verify your business and complete payments compliance.
                </span>
                <div className='position-relative'>
                    <Input disabled={!!(validating || validationData)} type="text" placeholder="Enter PAN Number" className="mt-3" value={panNumber} onChange={handleChange} onBlur={handleBlur} />
                    <div className="status-icon">
                        {
                            validating ? (
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            ) : (
                                validationData ? (
                                    validationData.status ? (
                                        <div className='d-flex gap-2'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z" fill="#0BAC68" />
                                            </svg>
                                        </div>

                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM13.4142 12L16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289C16.3166 6.90237 15.6834 6.90237 15.2929 7.29289L12 10.5858L8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L10.5858 12L7.29289 15.2929C6.90237 15.6834 6.90237 16.3166 7.29289 16.7071C7.68342 17.0976 8.31658 17.0976 8.70711 16.7071L12 13.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L13.4142 12Z" fill="#E03E2F" />
                                        </svg>
                                    )
                                ) : (
                                    panNumber && panError &&
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM13.4142 12L16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289C16.3166 6.90237 15.6834 6.90237 15.2929 7.29289L12 10.5858L8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L10.5858 12L7.29289 15.2929C6.90237 15.6834 6.90237 16.3166 7.29289 16.7071C7.68342 17.0976 8.31658 17.0976 8.70711 16.7071L12 13.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L13.4142 12Z" fill="#E03E2F" />
                                    </svg>
                                )
                            )
                        }
                    </div>

                </div>
                <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
                    <span>
                        {
                            panError && <div className="text-danger">{panError}</div>
                        }
                    </span>
                    <span className='cursor-pointer text-primary' onClick={() => onRegisteredForGST?.()}>We have registered for GST.</span>
                </div>

                {
                    validationData &&
                    <div className='mt-3 d-flex flex-column gap-2 mb-3'>
                        <div className="d-flex gap-2">
                            <span className="business-details-label">Registered Name:</span>
                            <span className="business-details-value text-success">{validationData.pan.registered_name}</span>
                        </div>
                        <div className="d-flex gap-2">
                            <span className="business-details-label">PAN Type:</span>
                            <span className="business-details-value text-success">{validationData.pan.type}</span>
                        </div>
                    </div>
                }
            </div>
            {
                validationData?.status ? (
                    <button onClick={addPANNumber} disabled={!isValidPANNumber} className='btn btn-gradient w-100'>
                        {
                            adding ? 'Confirming...' : 'Confirm'
                        }
                    </button>
                ) : (
                    <button onClick={validatePAN} disabled={!isValidPANNumber} className='btn btn-gradient w-100'>
                        {
                            validating ? 'Validating...' : 'Validate'
                        }
                    </button>
                )

            }
        </>
    )
}

export default PANInput
